import { useState } from "react";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { RhythmGrid, RhythmCell } from "@/components/RhythmGrid";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

type Pattern = "pulse" | "toggle" | "rock" | "tresillo";

// Pattern 1 — Pulse. Both root and octave together on every beat.
const pulse: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["D2", "D3"], caption: "1" },
  { hand: "L", on: true, pitches: ["D2", "D3"], caption: "2" },
  { hand: "L", on: true, pitches: ["D2", "D3"], caption: "3" },
  { hand: "L", on: true, pitches: ["D2", "D3"], caption: "4" },
];

// Pattern 2 — Toggle Pulse. Alternate root, octave, root, octave.
const toggle: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["D2"], caption: "Root" },
  { hand: "L", on: true, pitches: ["D3"], caption: "Oct" },
  { hand: "L", on: true, pitches: ["D2"], caption: "Root" },
  { hand: "L", on: true, pitches: ["D3"], caption: "Oct" },
  { hand: "L", on: true, pitches: ["D2"], caption: "Root" },
  { hand: "L", on: true, pitches: ["D3"], caption: "Oct" },
  { hand: "L", on: true, pitches: ["D2"], caption: "Root" },
  { hand: "L", on: true, pitches: ["D3"], caption: "Oct" },
];

// Pattern 3 — We Will Rock You. Stomp-stomp-clap.
const rock: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["D2", "D3"], caption: "stomp" },
  { hand: "L", on: true, pitches: ["D2", "D3"], caption: "stomp" },
  { hand: "L", on: false, pitches: [], caption: "clap" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: true, pitches: ["D2", "D3"], caption: "stomp" },
  { hand: "L", on: true, pitches: ["D2", "D3"], caption: "stomp" },
  { hand: "L", on: false, pitches: [], caption: "clap" },
  { hand: "L", on: false, pitches: [], caption: "—" },
];

// Pattern 4 — Tresillo (Afro-Cuban). Pinky-thumb-thumb pattern: 3+3+2 sixteenths.
const tresillo: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["D2"], caption: "1 (pinky)" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: true, pitches: ["D3"], caption: "&-2 (thumb)" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: false, pitches: [], caption: "—" },
  { hand: "L", on: true, pitches: ["D3"], caption: "4 (thumb)" },
  { hand: "L", on: false, pitches: [], caption: "—" },
];

export default function PianoFoundation12() {
  const { lesson, course, accent } = findLesson("piano-foundation", "12");
  const [pattern, setPattern] = useState<Pattern>("pulse");

  const grid =
    pattern === "pulse" ? pulse :
    pattern === "toggle" ? toggle :
    pattern === "rock" ? rock : tresillo;

  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Right hand keeps a melody — <strong>Mary Had a Little Lamb</strong> in D major works well. Left hand stays on the root and its octave.</Highlight>
        <Highlight>Four go-to rhythms with just <strong>two notes</strong> (root + octave): <strong>Pulse</strong>, <strong>Toggle Pulse</strong>, <strong>We Will Rock You</strong>, <strong>Tresillo</strong>.</Highlight>
        <Highlight>The "We Will Rock You" pattern is two stomps + a clap (root-root, then a beat of silence).</Highlight>
        <Highlight>Tresillo is a famous Afro-Cuban rhythm — pinky · thumb · thumb. Once you have it, you'll hear it in countless modern pop and Latin tracks.</Highlight>
        <Highlight>Don't obsess over a metronome. Build an <strong>internal clock</strong> first; the metronome calibrates that clock later, not the other way round.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The premise" title="One melody, four left-hand patterns" accent={accent.hex}>
        <p>
          Pick a tune you know. the choice here is Mary Had a Little Lamb in D major — but
          any melody works. Hold the right hand steady on that tune. Now we work the left
          hand through four different rhythms, all using the same two notes:{" "}
          <strong>D and the D an octave above</strong>.
        </p>

        <KeyIdea>
          One of the two hands always has to be on autopilot. Either the melody is so
          embedded in your right hand that it plays itself, or the LH rhythm is so embedded
          that it plays itself. The brain only has bandwidth for one new thing at a time.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Try the four patterns" title="Toggle between them — same two notes, four feels" accent={accent.hex}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button onClick={() => setPattern("pulse")} className={pattern === "pulse" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Pulse</button>
          <button onClick={() => setPattern("toggle")} className={pattern === "toggle" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Toggle Pulse</button>
          <button onClick={() => setPattern("rock")} className={pattern === "rock" ? "nsm-btn-primary" : "nsm-btn-ghost"}>We Will Rock You</button>
          <button onClick={() => setPattern("tresillo")} className={pattern === "tresillo" ? "nsm-btn-primary" : "nsm-btn-ghost"}>Tresillo</button>
        </div>

        <RhythmGrid
          cells={grid}
          subdivisions={pattern === "toggle" || pattern === "tresillo" || pattern === "rock" ? 2 : 1}
          beats={4}
          bpm={92}
          label={
            pattern === "pulse" ? "Pulse · root + octave together on every beat (the audience-clap)" :
            pattern === "toggle" ? "Toggle Pulse · alternating root and octave on every beat" :
            pattern === "rock" ? "We Will Rock You · stomp-stomp-clap (then silence)" :
            "Tresillo · pinky · thumb · thumb (3 + 3 + 2 across the bar)"
          }
        />

        <p className="prose-nsm text-sm">
          The hand stays parked on the same two notes for all four. Only the timing changes.
          That's the whole game of LH rhythm vocabulary.
        </p>
      </Insight>

      <Insight eyebrow="Pattern 1 · Pulse" title="The most important thing on the piano" accent={accent.hex}>
        <Pull>
          The pulse is always how the audience is going to clap to the music — how you
          yourself will probably clap, or just have these involuntary actions like
          tapping your foot or snapping or something, you know.
        </Pull>

        <p>
          With both root and octave together, your left hand thumps every beat: D-D, D-D,
          D-D, D-D. Steady, simple, unstoppable. The right hand floats Mary Had a Little Lamb
          on top.
        </p>
      </Insight>

      <Insight eyebrow="Pattern 2 · Toggle Pulse" title="Alternate the two notes" accent={accent.hex}>
        <p>
          Same beat density as Pulse, but instead of hitting both notes together, you{" "}
          <strong>alternate</strong>. Pinky on the lower D. Thumb on the upper D. Pinky.
          Thumb. The hand rocks gently between the two — the rhythm has a slight bounce that
          Pulse doesn't.
        </p>

        <Tip>
          Toggle Pulse keeps your hand moving while still being predictable. It's a great
          first step away from the static Pulse — useful any time the song wants a hint of
          motion in the bass without changing the rhythmic density.
        </Tip>
      </Insight>

      <Insight eyebrow="Pattern 3 · We Will Rock You" title="The most famous groove ever invented" accent={accent.hex}>
        <p>
          Two stomps, a clap, then space. <strong>D-D · (rest) · — · D-D · (rest) · —</strong>.
          The classic Queen pattern translated to LH piano. Pinky and thumb thump together
          twice, then the bar lets the right hand carry the rest.
        </p>

        <p>
          Even if you're not playing "We Will Rock You" itself — you'll be playing Mary Had a
          Little Lamb on top — the LH rhythm gives the song an instantly recognisable
          stadium-rock feel.
        </p>
      </Insight>

      <Insight eyebrow="Pattern 4 · Tresillo" title="The Afro-Cuban rhythm that powers modern pop" accent={accent.hex}>
        <p>
          Pinky on the root for the first hit. Thumb on the octave for the next two hits. A
          three-three-two rhythm across the bar. Once you know what to listen for, you'll
          hear Tresillo in a huge slice of contemporary music — Latin, hip-hop, modern pop —
          almost everything that feels "now".
        </p>

        <Tip>
          Counting tip: "ONE-and-a · two-AND-a · three-and-A · four-and-a". The accents fall
          on positions 1, the "and-of-2", and beat 4. Your left hand lands on each of those.
        </Tip>
      </Insight>

      <Insight eyebrow="Practice path" title="One pattern per session — and one melody" accent={accent.hex}>
        <Steps>
          <Step>
            Pick the melody. Mary Had a Little Lamb is fine. Play it slowly in the right hand
            until it's effortless. No left hand yet.
          </Step>
          <Step>
            Add the <strong>Pulse</strong> pattern. RH plays the melody; LH plays D-D on every
            beat. If the RH stumbles, the LH is going too fast — slow down.
          </Step>
          <Step>
            Switch to <strong>Toggle Pulse</strong>. Alternating root-octave under the same
            melody.
          </Step>
          <Step>
            Switch to <strong>We Will Rock You</strong>. Two stomps and a clap. The silences
            in this rhythm are part of the pattern — don't fill them.
          </Step>
          <Step>
            Switch to <strong>Tresillo</strong>. The trickiest of the four. Slow it right
            down at first.
          </Step>
        </Steps>

        <Pull>
          Don't obsess over the metronome. It is not important, especially when you're
          new to the instrument. Develop your own internal time.
        </Pull>
      </Insight>

      <Insight eyebrow="Then change melodies" title="The patterns are completely independent of the tune" accent={accent.hex}>
        <p>
          Once the four patterns are stable under Mary Had a Little Lamb, swap the right
          hand. Try Yankee Doodle. Try Twinkle Twinkle. Try a chorus melody from any pop song
          you know. The four LH rhythms work under <em>any</em> right-hand melody — that's
          why they're worth drilling.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-13"
        label="Lesson 13 · Tonic, Pre-Dominant and Dominant Chords"
        why="The LH has rhythm vocabulary now. Next we add harmonic vocabulary — the three chords (I, IV, V) most pop songs are built from."
      />
    </LessonShell>
  );
}
